import React, { Fragment, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { createRoot } from "react-dom/client";

import { AppsSDKUIProvider } from "@openai/apps-sdk-ui/components/AppsSDKUIProvider";
import { Badge } from "@openai/apps-sdk-ui/components/Badge";
import { Button } from "@openai/apps-sdk-ui/components/Button";
import { ButtonLink } from "@openai/apps-sdk-ui/components/Button";
import { Checkbox } from "@openai/apps-sdk-ui/components/Checkbox";

type DisplayMode = "pip" | "inline" | "fullscreen";
type Theme = "light" | "dark";

type SafeAreaInsets = { top: number; bottom: number; left: number; right: number };
type SafeArea = { insets: SafeAreaInsets };
type OpenAiGlobals = {
  theme: Theme;
  displayMode: DisplayMode;
  maxHeight: number;
  safeArea: SafeArea;
  toolInput: any;
  toolOutput: any;
  widgetState: any;
};

interface Flight {
  leavingAtString?: string;
  arrivingAtString?: string;
  leavingAt?: Date;
  arrivingAt?: Date;
  fromAirport?: string;
  toAirport?: string;
  fromAirportName?: string;
  toAirportName?: string;
  airlineCode?: string;
  airlineName?: string;
  codeshare?: boolean;
  operatingAirlineCode?: string;
  operatingAirlineName?: string;
  flightNumber?: string;
  aircraftName?: string;
  wifi_available?: boolean | null;
  wifi_cost?: number | string | null;
  power_available?: boolean | null;
  duration?: number;
  stopoverDuration?: number | null;
}

interface Trip {
  duration?: number;
  totalStops?: number;
  flights?: Flight[];
}

interface Itinerary {
  id?: string | number;
  offer_id?: string;
  price?: number;
  cabin_class?: string;
  co2_emissions?: number;
  is_change_allowed?: boolean | null;
  fare_change_fee?: number | null;
  fare_change_fee_currency?: string | null;
  is_cancel_allowed?: boolean | null;
  fare_cancel_fee?: number | null;
  fare_cancel_fee_currency?: string | null;
  carry_on_baggage_included?: number | null;
  checked_baggage_included?: number | null;
  totalStops?: number;
  fromAirport?: string;
  toAirport?: string;
  favourite?: boolean;
  airlineName?: string;
  airlineCode?: string;
  outboundTrip?: Trip;
  returnTrip?: Trip;
}

type PassengerCounts = {
  adults?: number;
  children?: number;
  infants?: number;
};

type ItineraryRequestSummary = {
  from_airports?: string[];
  to_airports?: string[];
  from_date?: Date;
  return_date?: Date;
  passengers?: PassengerCounts;
};

declare global {
  interface Window {
    openai: {
      theme?: Theme;
      displayMode?: DisplayMode;
      maxHeight?: number;
      safeArea?: SafeArea;
      toolInput?: any;
      toolOutput?: any;
      widgetState?: any;
      callTool: (name: string, args: any) => Promise<any>;
      sendFollowUpMessage: (args: { prompt: string }) => Promise<void>;
      requestDisplayMode: (args: { mode: DisplayMode }) => Promise<{ mode: DisplayMode }>;
      setWidgetState: (state: any) => Promise<void>;
    };
  }
}

const SET_GLOBALS_EVENT_TYPE = "openai:set_globals";
const CLOSE_ICON_TOOLTIPS_EVENT_TYPE = "itineraries:close_icon_tooltips";

function safeParse(v: any) {
  if (typeof v !== "string") return v;
  try {
    return JSON.parse(v);
  } catch {
    return {};
  }
}

function parseDate(ymd?: string): Date | undefined {
  if (!ymd) return;
  const m = ymd.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return;
  const [, y, mo, d] = m.map(Number);
  return new Date(Date.UTC(y, mo - 1, d));
}

function parseLocalDateTime(iso?: string): Date | undefined {
  if (!iso) return;
  const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/);
  if (!m) return;
  const [_, y, M, d, h, min] = m.map(Number);
  return new Date(Date.UTC(y, M - 1, d, h, min));
}

function parseDatesInTrip(trip?: Trip): Trip | undefined {
  if (!trip) return undefined;
  return {
    ...trip,
    flights: (trip.flights ?? []).map((f) => ({
      ...f,
      leavingAtString: (f as any).leavingAt,
      arrivingAtString: (f as any).arrivingAt,
      leavingAt: parseLocalDateTime((f as any).leavingAt),
      arrivingAt: parseLocalDateTime((f as any).arrivingAt),
    })),
  };
}

function fmtCurrency(n?: number) {
  return n == null
    ? ""
    : new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      currencyDisplay: "symbol",
      maximumFractionDigits: 0,
    }).format(n);
}

function formatCabinClass(cabinClass?: string) {
  switch (cabinClass?.toLowerCase()) {
    case "economy":
      return "Economy";
    case "premium_economy":
      return "Premium Economy";
    case "business":
      return "Business";
    case "first":
      return "First";
    default:
      return "";
  }
}

function normalizeBoolean(v: unknown): boolean | null {
  if (typeof v === "boolean") return v;
  if (typeof v === "string") {
    const s = v.trim().toLowerCase();
    if (s === "true") return true;
    if (s === "false") return false;
  }
  return null;
}

function normalizeNumber(v: unknown): number | null {
  if (typeof v === "number" && Number.isFinite(v)) return v;
  if (typeof v === "string" && v.trim() !== "") {
    const n = Number(v);
    if (Number.isFinite(n)) return n;
  }
  return null;
}

function formatChangeAllowed(v: unknown, fee: unknown) {
  const allowed = normalizeBoolean(v);
  const feeValue = normalizeNumber(fee);
  if (allowed === true && feeValue == null) return "Allowed for a fee";
  if (allowed === true) return "Allowed";
  if (allowed === false) return "Not allowed";
  return "Unknown";
}

function formatMoneyWithCurrency(v: unknown, currency: unknown) {
  const feeValue = normalizeNumber(v);
  if (feeValue == null) return "N/A";
  if (feeValue === 0) return "No fee";
  if (typeof currency === "string" && currency.trim() !== "") {
    return `${feeValue} ${currency.trim().toUpperCase()}`;
  }
  return fmtCurrency(feeValue);
}

function formatCancelAllowed(v: unknown, fee: unknown) {
  const allowed = normalizeBoolean(v);
  const feeValue = normalizeNumber(fee);
  if (allowed === true && feeValue == null) return "Allowed for a fee";
  if (allowed === true) return "Allowed";
  if (allowed === false) return "Not allowed";
  return "Unknown";
}

function formatCancellationWithOptionalFee(allowed: unknown, fee: unknown, currency: unknown) {
  const base = formatCancelAllowed(allowed, fee);
  const feeValue = normalizeNumber(fee);
  if (feeValue == null) return base;

  return `${base} (${formatMoneyWithCurrency(fee, currency)})`;
}

function formatChangesWithOptionalFee(allowed: unknown, fee: unknown, currency: unknown) {
  const base = formatChangeAllowed(allowed, fee);
  const feeValue = normalizeNumber(fee);
  if (feeValue == null) return base;

  return `${base} (${formatMoneyWithCurrency(fee, currency)})`;
}

function formatBaggageIncluded(v: unknown, label: string) {
  const quantity = normalizeNumber(v);
  if (quantity == null) return `${label}: Unknown`;
  if (quantity <= 0) return `${label}: Not included`;
  return `${label}: Included (${quantity} ${quantity === 1 ? "piece" : "pieces"})`;
}

function formatCo2Emissions(v: unknown) {
  const value = normalizeNumber(v);
  if (value == null) return "Unknown";
  return `${value} kg CO2`;
}

function fmtHM(secs?: number) {
  if (secs == null) return "";
  const h = Math.floor(secs / 3600);
  const m = Math.round((secs % 3600) / 60);
  if (m === 0) return `${h}h`;
  return `${h}h\u00A0${m.toString().padStart(2, "0")}m`;
}

function formatTime(d?: Date) {
  if (!d) return "";
  const hh = d.getUTCHours();
  const mm = d.getUTCMinutes();
  const ampm = hh >= 12 ? "pm" : "am";
  const hour12 = hh % 12 || 12;
  return `${hour12.toString().padStart(2, "0")}:${mm.toString().padStart(2, "0")}${ampm}`;
}

function formatDate(d?: Date) {
  if (!d) return "";
  const months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  return `${months[d.getUTCMonth()]} ${d.getUTCDate()}`;
}

function formatDayOffset(from?: Date, to?: Date) {
  if (!from || !to) return "";
  const fromMidnightUTC = Date.UTC(from.getUTCFullYear(), from.getUTCMonth(), from.getUTCDate());
  const toMidnightUTC = Date.UTC(to.getUTCFullYear(), to.getUTCMonth(), to.getUTCDate());
  const diffDays = Math.round((toMidnightUTC - fromMidnightUTC) / 86400000);
  return diffDays > 0 ? ` (+${diffDays})` : "";
}

function passengerLabel(count: number, singular: string, plural: string) {
  return `${count} ${count === 1 ? singular : plural}`;
}

function formatPassengers(passengers?: PassengerCounts) {
  const adults = Number(passengers?.adults ?? 1);
  const children = Number(passengers?.children ?? 0);
  const infants = Number(passengers?.infants ?? 0);

  const parts: string[] = [];
  if (adults > 0) parts.push(passengerLabel(adults, "adult", "adults"));
  if (children > 0) parts.push(passengerLabel(children, "child", "children"));
  if (infants > 0) parts.push(passengerLabel(infants, "infant", "infants"));

  return parts.length ? parts.join(" · ") : "1 adult";
}

function firstFlight(t?: Trip) {
  return t?.flights?.[0];
}
function lastFlight(t?: Trip) {
  return t?.flights && t.flights.length ? t.flights[t.flights.length - 1] : undefined;
}

function hourOfDay(date?: Date): number | null {
  return date ? date.getUTCHours() + date.getUTCMinutes() / 60 : null;
}

function isWithinTimeRange(date: Date | undefined, start: number, end: number) {
  const hour = hourOfDay(date);
  if (hour == null) return start === 0 && end === 24;
  return hour >= start && hour <= end;
}

function hoursFromDepartureDay(date?: Date, departure?: Date): number | null {
  if (!date || !departure) return null;
  const departureDay = Date.UTC(departure.getUTCFullYear(), departure.getUTCMonth(), departure.getUTCDate());
  return (date.getTime() - departureDay) / 3600000;
}

function isWithinTripTimeRange(date: Date | undefined, departure: Date | undefined, start: number, end: number) {
  const hour = hoursFromDepartureDay(date, departure);
  if (hour == null) return start === 0;
  return hour >= start && hour <= end;
}

function formatFilterHour(hour: number) {
  const day = Math.floor(hour / 24);
  const clockHour = hour % 24;
  const time = clockHour === 0
    ? "12 AM"
    : clockHour === 12
      ? "12 PM"
      : `${clockHour > 12 ? clockHour - 12 : clockHour} ${clockHour > 12 ? "PM" : "AM"}`;
  return day > 0 ? `${time} +${day} ${day === 1 ? "day" : "days"}` : time;
}

function codeshareOperators(itinerary: Itinerary): Flight[] {
  const flights = [
    ...(itinerary.outboundTrip?.flights ?? []),
    ...(itinerary.returnTrip?.flights ?? []),
  ];
  const seen = new Set<string>();

  return flights.filter((flight) => {
    if (!flight.codeshare || !flight.operatingAirlineName) return false;

    const key = String(flight.operatingAirlineCode || flight.operatingAirlineName)
      .trim()
      .toUpperCase();
    if (!key || seen.has(key)) return false;

    seen.add(key);
    return true;
  });
}

const AIRLINE_LOGOS_REQUIRING_WHITE_BACKGROUND = new Set([
  "UA",
  "QR",
  "3K",
  "7G",
  "A9",
  "AM",
  "AS",
  "B6",
  "LH",
  "LO",
  "6E",
  "KE",
  "PD",
  "AY",
  "BT",
  "CY",
  "GR",
  "JU",
  "LY",
  "MS",
  "NH",
  "RA",
  "T5",
  "NZ",
  "TR",
  "FJ",
  "IE",
]);

function airlineLogoUrl(code?: string, baseUrl?: string) {
  const c = String(code ?? "").trim().toUpperCase();
  if (!c) return null;
  const root = String(baseUrl ?? "").trim().replace(/\/+$/, "");
  if (!root) return null;
  return `${root}/airline-logos/v1/${c}.svg`;
}

function airlineLogoRequiresWhiteBackground(code?: string) {
  return AIRLINE_LOGOS_REQUIRING_WHITE_BACKGROUND.has(String(code ?? "").trim().toUpperCase());
}

function stopoverCodes(trip?: Trip): string[] {
  const flights = trip?.flights ?? [];
  if (flights.length <= 1) return [];
  return flights.slice(0, -1).map((f) => f.toAirport).filter(Boolean) as string[];
}

function totalLayoverDuration(trip?: Trip): number {
  return (trip?.flights ?? []).reduce(
    (total, flight) => total + (normalizeNumber(flight.stopoverDuration) ?? 0),
    0,
  );
}

const StopsBadge = ({
                      stops,
                      stopCodes,
                    }: {
  stops?: number;
  stopCodes?: string[];
}) => {
  const s = stops ?? 0;

  if (s === 0) {
    return <Badge color="success">Non-stop</Badge>;
  }

  return (
    <Badge color="warning" className="flex items-center gap-1 max-w-full">
      <span>{s === 1 ? "1 stop" : `${s} stops`}</span>

      {stopCodes?.length ? (
        <span className="hidden md:inline text-[11px] opacity-80 truncate">
          · {stopCodes.join(" · ")}
        </span>
      ) : null}
    </Badge>
  );
};
const DurationBadge = ({ seconds }: { seconds?: number }) => {
  return (
    <Badge color="secondary">
      {fmtHM(seconds)}
    </Badge>
  );
};

function TappableTooltip({ content, children }: { content: React.ReactNode; children: React.ReactElement }) {
  const [tapOpen, setTapOpen] = useState(false);
  const [hoverOpen, setHoverOpen] = useState(false);
  const [position, setPosition] = useState<{ left: number; top: number } | null>(null);
  const triggerRef = useRef<HTMLElement | null>(null);
  const tooltipRef = useRef<HTMLDivElement | null>(null);
  const lastPointerActivationRef = useRef(0);
  const tooltipId = useId();
  const iconColor = document.documentElement.getAttribute("data-theme") === "dark" ? "#cbd5e1" : "#7b8492";
  const open = tapOpen || hoverOpen;

  const updatePosition = () => {
    const trigger = triggerRef.current;
    const tooltip = tooltipRef.current;
    if (!trigger || !tooltip) return;

    const triggerRect = trigger.getBoundingClientRect();
    const tooltipRect = tooltip.getBoundingClientRect();
    const viewportWidth = document.documentElement.clientWidth;
    const centeredLeft = triggerRect.left + (triggerRect.width - tooltipRect.width) / 2;
    const left = Math.min(Math.max(8, centeredLeft), Math.max(8, viewportWidth - tooltipRect.width - 8));
    const above = triggerRect.top - tooltipRect.height - 5;
    const top = above >= 8 ? above : triggerRect.bottom + 5;
    setPosition({ left, top });
  };

  useLayoutEffect(() => {
    if (!open) {
      setPosition(null);
      return;
    }
    updatePosition();
  }, [open, content]);

  useEffect(() => {
    if (!open) return;

    const closeOnOutsidePointer = (event: PointerEvent) => {
      const target = event.target as Node;
      if (!triggerRef.current?.contains(target) && !tooltipRef.current?.contains(target)) {
        setTapOpen(false);
        setHoverOpen(false);
      }
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setTapOpen(false);
        setHoverOpen(false);
      }
    };

    document.addEventListener("pointerdown", closeOnOutsidePointer);
    document.addEventListener("keydown", closeOnEscape);
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);
    return () => {
      document.removeEventListener("pointerdown", closeOnOutsidePointer);
      document.removeEventListener("keydown", closeOnEscape);
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [open]);

  useEffect(() => {
    const close = () => {
      setTapOpen(false);
      setHoverOpen(false);
    };
    document.addEventListener(CLOSE_ICON_TOOLTIPS_EVENT_TYPE, close);
    return () => document.removeEventListener(CLOSE_ICON_TOOLTIPS_EVENT_TYPE, close);
  }, []);

  const toggle = () => {
    if (!tapOpen) document.dispatchEvent(new Event(CLOSE_ICON_TOOLTIPS_EVENT_TYPE));
    setTapOpen((current) => !current);
  };

  return (
    <>
      {React.cloneElement(children, {
        ref: triggerRef,
        role: "button",
        tabIndex: 0,
        "aria-describedby": open ? tooltipId : undefined,
        "data-itinerary-tooltip-trigger": "",
        className: `${children.props.className ?? ""} appearance-none border-0 bg-transparent p-0 shadow-none`,
        style: {
          ...children.props.style,
          color: iconColor,
          background: "transparent",
          boxShadow: "none",
        },
        onPointerEnter: (event: React.PointerEvent) => {
          if (event.pointerType !== "touch") setHoverOpen(true);
        },
        onPointerLeave: (event: React.PointerEvent) => {
          if (event.pointerType !== "touch") setHoverOpen(false);
        },
        onPointerDown: (event: React.PointerEvent) => {
          event.stopPropagation();
        },
        onPointerUp: (event: React.PointerEvent) => {
          event.stopPropagation();
          if (event.pointerType === "touch" || event.pointerType === "pen") {
            event.preventDefault();
            lastPointerActivationRef.current = Date.now();
            toggle();
          }
        },
        onClick: (event: React.MouseEvent) => {
          event.stopPropagation();
          if (Date.now() - lastPointerActivationRef.current < 750) return;
          toggle();
        },
        onKeyDown: (event: React.KeyboardEvent) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            event.stopPropagation();
            toggle();
          }
        },
      } as any)}
      {open && createPortal(
        <div
          ref={tooltipRef}
          id={tooltipId}
          role="tooltip"
          className="itinerary-icon-tooltip"
          style={{
            left: position?.left ?? 0,
            top: position?.top ?? 0,
            visibility: position ? "visible" : "hidden",
          }}
        >
          {content}
        </div>,
        document.body,
      )}
    </>
  );
}

const DualRangeSlider = ({
  min,
  max,
  step,
  start,
  end,
  label,
  formatValue,
  allowEqual = false,
  onChange,
}: {
  min: number;
  max: number;
  step: number;
  start: number;
  end: number;
  label: string;
  formatValue: (value: number) => string;
  allowEqual?: boolean;
  onChange: (start: number, end: number) => void;
}) => {
  const stopCount = Math.min(10, Math.max(1, Math.round((max - min) / step)));
  const startPosition = (start - min) / (max - min || 1);
  const endPosition = (end - min) / (max - min || 1);
  const labelPosition = (position: number) => `calc(9px + (100% - 18px) * ${position})`;
  const startLabelOffset = Math.min(50, (startPosition / 0.15) * 50);
  const handlesOverlap = start === end;
  const minimumHandleOnTop = handlesOverlap && start > min;

  return (
    <div className="relative h-11">
      <span
        aria-hidden="true"
        className="absolute top-0 z-40 whitespace-nowrap rounded bg-surface px-1 text-center text-[10px] font-medium leading-4 tabular-nums text-secondary shadow-sm"
        style={{
          left: start === min ? 0 : labelPosition(startPosition),
          transform: start === min ? undefined : `translateX(-${startLabelOffset}%)`,
        }}
      >
        {formatValue(start)}
      </span>
      <span
        aria-hidden="true"
        className="absolute top-0 z-40 -translate-x-1/2 whitespace-nowrap rounded bg-surface px-1 text-center text-[10px] font-medium leading-4 tabular-nums text-secondary shadow-sm"
        style={{ left: labelPosition(endPosition) }}
      >
        {formatValue(end)}
      </span>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={start}
        aria-label={`${label} minimum`}
        aria-valuetext={formatValue(start)}
        className={`itinerary-range absolute inset-x-0 top-5 h-6 w-full cursor-pointer ${minimumHandleOnTop ? "z-40" : "z-30"}`}
        onChange={(e) => onChange(Math.min(Number(e.target.value), end - (allowEqual ? 0 : step)), end)}
      />
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={end}
        aria-label={`${label} maximum`}
        aria-valuetext={formatValue(end)}
        className={`itinerary-range absolute inset-x-0 top-5 h-6 w-full cursor-pointer ${handlesOverlap && !minimumHandleOnTop ? "z-40" : "z-30"}`}
        onChange={(e) => onChange(start, Math.max(Number(e.target.value), start + (allowEqual ? 0 : step)))}
      />
      <span aria-hidden="true" className="itinerary-range-rail pointer-events-none absolute left-[9px] right-[9px] top-8 z-0 -translate-y-1/2" />
      <div aria-hidden="true" className="pointer-events-none absolute left-[9px] right-[9px] top-5 z-20 h-6">
        {Array.from({ length: stopCount + 1 }, (_, index) => (
          <span
            key={index}
            style={{ left: `${(index / stopCount) * 100}%` }}
            className="itinerary-range-stop absolute top-1/2 h-2 w-0.5 -translate-x-1/2 -translate-y-1/2 rounded-full"
          />
        ))}
      </div>
    </div>
  );
};

const TimeRangeControl = ({
  label,
  max = 24,
  start,
  end,
  onChange,
}: {
  label: string;
  max?: number;
  start: number;
  end: number;
  onChange: (start: number, end: number) => void;
}) => (
  <div>
    <div className="mb-2 text-xs text-secondary">{label}</div>
    <DualRangeSlider
        min={0}
        max={max}
        step={1}
        start={start}
        end={end}
        label={label}
        formatValue={formatFilterHour}
        onChange={(nextStart, nextEnd) => onChange(Math.min(nextStart, nextEnd - 1), Math.max(nextEnd, nextStart + 1))}
    />
  </div>
);

const NumberRangeControl = ({
  label,
  min,
  max,
  start,
  end,
  step,
  formatValue,
  allowEqual,
  onChange,
}: {
  label: string;
  min: number;
  max: number;
  start: number;
  end: number;
  step: number;
  formatValue: (value: number) => string;
  allowEqual?: boolean;
  onChange: (start: number, end: number) => void;
}) => (
  <div>
    <div className="mb-2 text-xs text-secondary">{label}</div>
    <DualRangeSlider min={min} max={max} step={step} start={start} end={end} label={label} formatValue={formatValue} allowEqual={allowEqual} onChange={onChange} />
  </div>
);

type SortKey =
  | "price"
  | "duration"
  | "stops"
  | "outbound_layover"
  | "return_layover"
  | "outbound_departure"
  | "outbound_arrival"
  | "return_departure"
  | "return_arrival";

export function ItineraryWidget({ toolOutputOverride, themeOverride }: { toolOutputOverride?: any; themeOverride?: Theme } = {}) {
  const systemTheme = (): Theme =>
    window.matchMedia?.("(prefers-color-scheme: dark)").matches ? "dark" : "light";

  const [theme, setTheme] = useState<Theme>(themeOverride ?? window.openai?.theme ?? systemTheme());
  const [toolOutput, setToolOutput] = useState<any>(toolOutputOverride ?? window.openai?.toolOutput ?? {});
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  // virtual scroll
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const [viewportHeight, setViewportHeight] = useState(400);
  const defaultRowHeight = 96;
  const overscan = 5;
  const rowSpacerHeight = 10;
  const [scrollTop, setScrollTop] = useState(0);
  const [rowHeights, setRowHeights] = useState<number[]>([]);
  const [virtualScrollLocked, setVirtualScrollLocked] = useState<boolean | null>(null);

  useEffect(() => {
    if (themeOverride) return;

    const pageBackground = theme === "dark" ? "#000" : "transparent";
    document.documentElement.setAttribute("data-theme", theme);
    document.documentElement.style.colorScheme = theme;
    document.documentElement.style.backgroundColor = pageBackground;
    document.body.style.backgroundColor = pageBackground;
    const root = document.getElementById("root");
    if (root) root.style.backgroundColor = pageBackground;
  }, [theme, themeOverride]);

  useEffect(() => {
    if (themeOverride) setTheme(themeOverride);
  }, [themeOverride]);

  useEffect(() => {
    if (toolOutputOverride !== undefined) setToolOutput(toolOutputOverride);
  }, [toolOutputOverride]);

  useEffect(() => {
    const onSetGlobals = (evt: Event) => {
      if (themeOverride) return;

      const e = evt as CustomEvent<{ globals: Partial<OpenAiGlobals> }>;
      const g = e.detail?.globals ?? {};
      if (g.theme) setTheme(g.theme);
      if (g.toolOutput) setToolOutput(g.toolOutput);
    };
    window.addEventListener(SET_GLOBALS_EVENT_TYPE, onSetGlobals as EventListener);

    const mql = window.matchMedia?.("(prefers-color-scheme: dark)");
    const onSystemThemeChange = (e: MediaQueryListEvent) => {
      if (themeOverride) return;
      if (window.openai?.theme) return;
      setTheme(e.matches ? "dark" : "light");
    };
    mql?.addEventListener?.("change", onSystemThemeChange);

    if (scrollContainerRef.current) {
      setViewportHeight(scrollContainerRef.current.clientHeight || 400);
    }

    return () => {
      window.removeEventListener(SET_GLOBALS_EVENT_TYPE, onSetGlobals as EventListener);
      mql?.removeEventListener?.("change", onSystemThemeChange);
    };
  }, [themeOverride]);

  const raw = useMemo(() => safeParse(toolOutput), [toolOutput]);
  const showWidget = !!raw?.show_widget;
  const showAirlineFilter = !!raw?.show_airline_filter;

  const fullDisplayUrl = raw?.full_display_url;
  const baseBookingUrl = raw?.base_booking_url as string | undefined;
  const baseUrl = raw?.base_url as string | undefined;

  const currentTotalItinerariesCount =
    typeof raw?.extra_info?.total_itineraries_count === "number"
      ? raw.extra_info.total_itineraries_count
      : undefined;
  const [lastTotalItinerariesCount, setLastTotalItinerariesCount] = useState(currentTotalItinerariesCount);

  useEffect(() => {
    if (currentTotalItinerariesCount != null) setLastTotalItinerariesCount(currentTotalItinerariesCount);
  }, [currentTotalItinerariesCount]);

  const totalItinerariesCount = currentTotalItinerariesCount ?? lastTotalItinerariesCount;

  const itineraryRequests = useMemo<ItineraryRequestSummary[]>(() => {
    const v = raw?.itinerary_requests;
    if (!v) return [];
    const arr = typeof v === "string" ? safeParse(v) : v;
    if (!Array.isArray(arr)) return [];
    return arr.map((r) => ({
      ...r,
      from_date: parseDate(r.from_date),
      return_date: parseDate(r.return_date),
      passengers: r.passengers,
    }));
  }, [raw]);

  const itineraries = useMemo<Itinerary[]>(() => {
    const next = raw?.next_best_itineraries ?? [];
    const prior = raw?.prior_itineraries ?? [];
    return [...prior, ...next]
      .filter(Boolean)
      .map((it: Itinerary) => ({
        ...it,
        outboundTrip: parseDatesInTrip(it.outboundTrip),
        returnTrip: parseDatesInTrip(it.returnTrip),
      }))
      .sort((a, b) => {
        const aPosition = Number(a.id);
        const bPosition = Number(b.id);
        if (!Number.isFinite(aPosition) || !Number.isFinite(bPosition)) return 0;
        return aPosition - bPosition;
      });
  }, [raw]);
  const showCabinClass = useMemo(
    () => new Set(itineraries.map((it) => it.cabin_class?.toLowerCase()).filter(Boolean)).size > 1,
    [itineraries],
  );
  const previousItineraryCountRef = useRef(itineraries.length);

  useEffect(() => {
    if (itineraries.length > previousItineraryCountRef.current) setExpanded({});
    previousItineraryCountRef.current = itineraries.length;
  }, [itineraries.length]);

  const airlineOptions = useMemo(() => {
    const m = new Map<string, { name: string; count: number }>();
    for (const it of itineraries) {
      if (!it.airlineCode) continue;
      const current = m.get(it.airlineCode);
      m.set(it.airlineCode, {
        name: it.airlineName ?? it.airlineCode,
        count: (current?.count ?? 0) + 1,
      });
    }
    return Array.from(m.entries(), ([code, airline]) => ({ code, ...airline }))
      .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));
  }, [itineraries]);

  const maxItineraryPrice = useMemo(
    () => Math.ceil(Math.max(0, ...itineraries.map((it) => it.price ?? 0)) / 50) * 50,
    [itineraries],
  );
  const minItineraryPrice = useMemo(
    () => {
      const prices = itineraries.map((it) => it.price).filter((price): price is number => price != null);
      return prices.length ? Math.floor(Math.min(...prices) / 25) * 25 : 0;
    },
    [itineraries],
  );
  const maxItineraryDurationHours = useMemo(
    () => Math.ceil(Math.max(
      0,
      ...itineraries.map((it) => ((it.outboundTrip?.duration ?? 0) + (it.returnTrip?.duration ?? 0)) / 3600),
    )),
    [itineraries],
  );
  const minItineraryDurationHours = useMemo(
    () => itineraries.length ? Math.floor(Math.min(
      ...itineraries.map((it) => ((it.outboundTrip?.duration ?? 0) + (it.returnTrip?.duration ?? 0)) / 3600),
    )) : 0,
    [itineraries],
  );
  const maxItineraryStops = useMemo(
    () => Math.max(0, ...itineraries.map((it) => it.totalStops ?? 0)),
    [itineraries],
  );
  const minItineraryStops = useMemo(
    () => itineraries.length ? Math.min(...itineraries.map((it) => it.totalStops ?? 0)) : 0,
    [itineraries],
  );
  const maxOutboundLayoverHours = useMemo(
    () => Math.ceil(Math.max(0, ...itineraries.map((it) => totalLayoverDuration(it.outboundTrip))) / 1800) / 2,
    [itineraries],
  );
  const maxReturnLayoverHours = useMemo(
    () => Math.ceil(Math.max(0, ...itineraries.map((it) => totalLayoverDuration(it.returnTrip))) / 1800) / 2,
    [itineraries],
  );
  const hasReturnTrips = itineraries.some((it) => it.returnTrip);
  const maxOutboundArrivalHour = useMemo(
    () => Math.max(24, ...itineraries.map((it) => {
      const first = firstFlight(it.outboundTrip);
      return Math.ceil(hoursFromDepartureDay(lastFlight(it.outboundTrip)?.arrivingAt, first?.leavingAt) ?? 24);
    })),
    [itineraries],
  );
  const maxReturnArrivalHour = useMemo(
    () => Math.max(24, ...itineraries.map((it) => {
      const first = firstFlight(it.returnTrip);
      return Math.ceil(hoursFromDepartureDay(lastFlight(it.returnTrip)?.arrivingAt, first?.leavingAt) ?? 24);
    })),
    [itineraries],
  );

  const [filters, setFilters] = useState({
    minStops: null as number | null,
    maxStops: null as number | null,
    airlines: [] as string[],
    minPrice: null as number | null,
    maxPrice: null as number | null,
    minDurationHours: null as number | null,
    maxDurationHours: null as number | null,
    minOutboundLayoverHours: null as number | null,
    maxOutboundLayoverHours: null as number | null,
    minReturnLayoverHours: null as number | null,
    maxReturnLayoverHours: null as number | null,
    outboundDepartureStart: 0,
    outboundDepartureEnd: 24,
    outboundArrivalStart: 0,
    outboundArrivalEnd: null as number | null,
    returnDepartureStart: 0,
    returnDepartureEnd: 24,
    returnArrivalStart: 0,
    returnArrivalEnd: null as number | null,
    changeable: false,
    refundable: false,
    carryOnIncluded: false,
    checkedBagIncluded: false,
    excludeCodeshares: false,
    sortKey: (itineraryRequests.length ? "price" : null) as SortKey | null,
    sortDir: "asc" as "asc" | "desc",
  });

  const filteredAndSorted = useMemo(() => {
    let arr = itineraries;

    if (filters.airlines.length) arr = arr.filter((it) => !!it.airlineCode && filters.airlines.includes(it.airlineCode));

    arr = arr.filter((it) => {
      const stops = it.totalStops ?? 0;
      return stops >= (filters.minStops ?? minItineraryStops) && stops <= (filters.maxStops ?? maxItineraryStops);
    });

    if (filters.changeable) {
      arr = arr.filter((it) => normalizeBoolean(it.is_change_allowed) === true);
    }
    if (filters.refundable) {
      arr = arr.filter((it) => normalizeBoolean(it.is_cancel_allowed) === true);
    }
    if (filters.carryOnIncluded) arr = arr.filter((it) => (normalizeNumber(it.carry_on_baggage_included) ?? 0) > 0);
    if (filters.checkedBagIncluded) arr = arr.filter((it) => (normalizeNumber(it.checked_baggage_included) ?? 0) > 0);
    if (filters.excludeCodeshares) {
      arr = arr.filter((it) => [
        ...(it.outboundTrip?.flights ?? []),
        ...(it.returnTrip?.flights ?? []),
      ].every((flight) => !flight.codeshare));
    }
    arr = arr.filter((it) => {
      const price = it.price ?? Number.POSITIVE_INFINITY;
      return price >= (filters.minPrice ?? minItineraryPrice) && price <= (filters.maxPrice ?? maxItineraryPrice);
    });
    if (filters.minDurationHours != null || filters.maxDurationHours != null) {
      arr = arr.filter((it) => {
        const duration = (it.outboundTrip?.duration ?? 0) + (it.returnTrip?.duration ?? 0);
        return duration >= (filters.minDurationHours ?? minItineraryDurationHours) * 3600 && duration <= (filters.maxDurationHours ?? maxItineraryDurationHours) * 3600;
      });
    }
    if (filters.minOutboundLayoverHours != null || filters.maxOutboundLayoverHours != null) {
      arr = arr.filter((it) => {
        const duration = totalLayoverDuration(it.outboundTrip);
        return duration >= (filters.minOutboundLayoverHours ?? 0) * 3600 && duration <= (filters.maxOutboundLayoverHours ?? maxOutboundLayoverHours) * 3600;
      });
    }
    if (filters.minReturnLayoverHours != null || filters.maxReturnLayoverHours != null) {
      arr = arr.filter((it) => {
        const duration = totalLayoverDuration(it.returnTrip);
        return duration >= (filters.minReturnLayoverHours ?? 0) * 3600 && duration <= (filters.maxReturnLayoverHours ?? maxReturnLayoverHours) * 3600;
      });
    }

    arr = arr.filter((it) => {
      const outboundFirst = firstFlight(it.outboundTrip);
      const outboundLast = lastFlight(it.outboundTrip);
      if (!isWithinTimeRange(outboundFirst?.leavingAt, filters.outboundDepartureStart, filters.outboundDepartureEnd)) return false;
      if (!isWithinTripTimeRange(outboundLast?.arrivingAt, outboundFirst?.leavingAt, filters.outboundArrivalStart, filters.outboundArrivalEnd ?? maxOutboundArrivalHour)) return false;

      if (it.returnTrip) {
        const returnFirst = firstFlight(it.returnTrip);
        const returnLast = lastFlight(it.returnTrip);
        if (!isWithinTimeRange(returnFirst?.leavingAt, filters.returnDepartureStart, filters.returnDepartureEnd)) return false;
        if (!isWithinTripTimeRange(returnLast?.arrivingAt, returnFirst?.leavingAt, filters.returnArrivalStart, filters.returnArrivalEnd ?? maxReturnArrivalHour)) return false;
      }
      return true;
    });

    if (!filters.sortKey) return arr;

    const sortValue = (it: Itinerary, key: SortKey): number => {
      switch (key) {
        case "price":
          return it.price ?? Number.POSITIVE_INFINITY;

        case "duration":
          return (it.outboundTrip?.duration ?? 0) + (it.returnTrip?.duration ?? 0);

        case "stops":
          return it.totalStops ?? 0;

        case "outbound_layover":
          return totalLayoverDuration(it.outboundTrip);

        case "return_layover":
          return totalLayoverDuration(it.returnTrip);

        case "outbound_departure":
          return firstFlight(it.outboundTrip)?.leavingAt?.getTime() ?? Number.POSITIVE_INFINITY;

        case "outbound_arrival":
          return lastFlight(it.outboundTrip)?.arrivingAt?.getTime() ?? Number.POSITIVE_INFINITY;

        case "return_departure":
          return firstFlight(it.returnTrip)?.leavingAt?.getTime() ?? Number.POSITIVE_INFINITY;

        case "return_arrival":
          return lastFlight(it.returnTrip)?.arrivingAt?.getTime() ?? Number.POSITIVE_INFINITY;
      }
    };

    return [...arr].sort((a, b) => {
      const av = sortValue(a, filters.sortKey!);
      const bv = sortValue(b, filters.sortKey!);
      return filters.sortDir === "asc" ? av - bv : bv - av;
    });
  }, [itineraries, filters]);

  const totalBlocks = filteredAndSorted.length;

  const useVirtualScrolling = useMemo(() => {
    if (virtualScrollLocked == null && totalBlocks) {
      setVirtualScrollLocked(totalBlocks > 50);
      return totalBlocks > 50;
    }
    return virtualScrollLocked ?? false;
  }, [totalBlocks, virtualScrollLocked]);

  const getRowHeight = (index: number) => rowHeights[index] ?? defaultRowHeight;

  const cumulativeHeights = useMemo(() => {
    const n = totalBlocks;
    const cum = new Array<number>(n + 1);
    let acc = 0;
    cum[0] = 0;
    for (let i = 0; i < n; i++) {
      acc += getRowHeight(i) + rowSpacerHeight;
      cum[i + 1] = acc;
    }
    return cum;
  }, [totalBlocks, rowHeights]);

  const findStartIndexByScrollTop = (scroll: number) => {
    const cum = cumulativeHeights;
    let lo = 0;
    let hi = cum.length - 1;
    while (lo < hi) {
      const mid = (lo + hi) >> 1;
      if (cum[mid] <= scroll) lo = mid + 1;
      else hi = mid;
    }
    return Math.max(0, lo - 1);
  };

  const startIndex = useMemo(() => {
    if (!totalBlocks) return 0;
    const base = findStartIndexByScrollTop(scrollTop);
    return Math.max(0, base - overscan);
  }, [totalBlocks, scrollTop, cumulativeHeights]);

  const visibleCount = useMemo(() => {
    const totalHeight = cumulativeHeights[cumulativeHeights.length - 1] || 1;
    const approximateRow = totalHeight / Math.max(totalBlocks || 1, 1);
    const rowsInViewport = Math.ceil(viewportHeight / approximateRow);
    return rowsInViewport + overscan * 2;
  }, [cumulativeHeights, totalBlocks, viewportHeight]);

  const endIndex = useMemo(() => {
    if (!totalBlocks) return 0;
    return Math.min(totalBlocks - 1, startIndex + visibleCount - 1);
  }, [totalBlocks, startIndex, visibleCount]);

  const topSpacerHeight = useMemo(() => (useVirtualScrolling ? cumulativeHeights[startIndex] ?? 0 : 0), [
    useVirtualScrolling,
    cumulativeHeights,
    startIndex,
  ]);

  const bottomSpacerHeight = useMemo(() => {
    if (!useVirtualScrolling) return 0;
    const total = cumulativeHeights[cumulativeHeights.length - 1] ?? 0;
    const endCum = cumulativeHeights[endIndex + 1] ?? total;
    return Math.max(0, total - endCum);
  }, [useVirtualScrolling, cumulativeHeights, endIndex]);

  const visibleItineraries = useMemo(() => {
    if (!totalBlocks) return [];
    if (!useVirtualScrolling) return filteredAndSorted;
    return filteredAndSorted.slice(startIndex, endIndex + 1);
  }, [filteredAndSorted, totalBlocks, useVirtualScrolling, startIndex, endIndex]);

  const toggleExpanded = (id: Itinerary["id"]) => {
    if (id == null) return;
    const key = String(id);
    setExpanded((p) => ({ ...p, [key]: !p[key] }));
  };
  const isExpanded = (id: Itinerary["id"]) => (id != null ? !!expanded[String(id)] : false);

  if (!showWidget) return <div style={{ height: 1 }} />;

  return (
    <div className="text-primary w-full" data-theme={theme}>
      <div className="mx-auto w-full max-w-[1000px] px-2">
        {/* Header */}
        {itineraryRequests.length > 0 && (
          <div className="mb-3 rounded-2xl border border-subtle bg-surface shadow-lg p-4">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <img src={`${String(baseUrl ?? "").replace(/\/+$/, "")}/images/logo_with_word_blue.png`} alt="Reagent" className="h-8 w-auto" />
              <div className="text-secondary text-sm">
                {filteredAndSorted.length < itineraries.length
                  ? `${filteredAndSorted.length} of ${itineraries.length} results`
                  : `${itineraries.length} results`}
              </div>
            </div>

            <ul className="mt-3 space-y-2">
              {itineraryRequests.map((r, idx: number) => {
                const from = (r.from_airports ?? []).map((a: string) => a.toUpperCase()).join(", ");
                const to = (r.to_airports ?? []).map((a: string) => a.toUpperCase()).join(", ");
                const dates = `${formatDate(r.from_date)}${r.return_date ? ` → ${formatDate(r.return_date)}` : ""}`;
                const passengers = formatPassengers(r.passengers);
                return (
                  <li key={idx} className="rounded-xl border border-subtle bg-surface px-3 py-2">
                    <div className="font-medium">{from || "—"} → {to || "—"}</div>
                    <div className="text-secondary text-sm">{dates}</div>
                    <div className="text-secondary text-sm">{passengers}</div>
                  </li>
                );
              })}
            </ul>
          </div>
        )}

        {/* Filters */}
        {showAirlineFilter && (
          <details className="group mb-3 rounded-2xl border border-subtle bg-surface shadow-lg">
            <summary className="flex cursor-pointer list-none items-center justify-between gap-3 px-4 py-3">
              <div>
                <div className="font-medium">Filter and sort</div>
                <div className="text-xs text-secondary">{filteredAndSorted.length} results</div>
              </div>
              <span className="text-secondary">
                <svg viewBox="0 0 20 20" className="h-4 w-4 transition-transform group-open:rotate-180" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="m5 7.5 5 5 5-5" />
                </svg>
              </span>
            </summary>
            <div className="border-t border-subtle p-4">
              <div className="grid gap-6 md:grid-cols-3 md:gap-x-10">
                <NumberRangeControl
                  label="Price"
                  min={minItineraryPrice}
                  max={Math.max(25, maxItineraryPrice)}
                  step={25}
                  start={filters.minPrice ?? minItineraryPrice}
                  end={filters.maxPrice ?? Math.max(25, maxItineraryPrice)}
                  formatValue={(value) => fmtCurrency(value)}
                  onChange={(start, end) => setFilters((p) => ({ ...p, minPrice: start, maxPrice: end >= Math.max(25, maxItineraryPrice) ? null : end }))}
                />
                <NumberRangeControl
                  label="Stops"
                  min={minItineraryStops}
                  max={Math.max(1, maxItineraryStops)}
                  step={1}
                  start={filters.minStops ?? minItineraryStops}
                  end={filters.maxStops ?? Math.max(1, maxItineraryStops)}
                  formatValue={(value) => `${value} ${value === 1 ? "stop" : "stops"}`}
                  allowEqual
                  onChange={(start, end) => setFilters((p) => ({ ...p, minStops: start, maxStops: end >= Math.max(1, maxItineraryStops) ? null : end }))}
                />
                <NumberRangeControl
                  label="Total duration"
                  min={minItineraryDurationHours}
                  max={Math.max(1, maxItineraryDurationHours)}
                  step={1}
                  start={filters.minDurationHours ?? minItineraryDurationHours}
                  end={filters.maxDurationHours ?? Math.max(1, maxItineraryDurationHours)}
                  formatValue={(value) => `${value}h`}
                  onChange={(start, end) => setFilters((p) => ({ ...p, minDurationHours: start, maxDurationHours: end >= Math.max(1, maxItineraryDurationHours) ? null : end }))}
                />
                <NumberRangeControl
                  label="Outbound layover"
                  min={0}
                  max={Math.max(0.5, maxOutboundLayoverHours)}
                  step={0.5}
                  start={filters.minOutboundLayoverHours ?? 0}
                  end={filters.maxOutboundLayoverHours ?? Math.max(0.5, maxOutboundLayoverHours)}
                  formatValue={(value) => fmtHM(value * 3600)}
                  allowEqual
                  onChange={(start, end) => setFilters((p) => ({ ...p, minOutboundLayoverHours: start > 0 ? start : null, maxOutboundLayoverHours: end >= Math.max(0.5, maxOutboundLayoverHours) ? null : end }))}
                />
                {hasReturnTrips && (
                  <NumberRangeControl
                    label="Return layover"
                    min={0}
                    max={Math.max(0.5, maxReturnLayoverHours)}
                    step={0.5}
                    start={filters.minReturnLayoverHours ?? 0}
                    end={filters.maxReturnLayoverHours ?? Math.max(0.5, maxReturnLayoverHours)}
                    formatValue={(value) => fmtHM(value * 3600)}
                    allowEqual
                    onChange={(start, end) => setFilters((p) => ({ ...p, minReturnLayoverHours: start > 0 ? start : null, maxReturnLayoverHours: end >= Math.max(0.5, maxReturnLayoverHours) ? null : end }))}
                  />
                )}
              </div>

              <div className="mt-4 grid gap-4 border-t border-subtle pt-4 md:grid-cols-2">
                <fieldset className="rounded-xl border border-subtle p-3">
                  <legend className="px-1 text-sm font-medium">Outbound times</legend>
                  <div className="grid gap-3 sm:grid-cols-2">
                    <TimeRangeControl label="Departure" start={filters.outboundDepartureStart} end={filters.outboundDepartureEnd} onChange={(start, end) => setFilters((p) => ({ ...p, outboundDepartureStart: start, outboundDepartureEnd: end }))} />
                    <TimeRangeControl label="Arrival" max={maxOutboundArrivalHour} start={filters.outboundArrivalStart} end={filters.outboundArrivalEnd ?? maxOutboundArrivalHour} onChange={(start, end) => setFilters((p) => ({ ...p, outboundArrivalStart: start, outboundArrivalEnd: end >= maxOutboundArrivalHour ? null : end }))} />
                  </div>
                </fieldset>
                <fieldset className="rounded-xl border border-subtle p-3">
                  <legend className="px-1 text-sm font-medium">Return times</legend>
                  <div className="grid gap-3 sm:grid-cols-2">
                    <TimeRangeControl label="Departure" start={filters.returnDepartureStart} end={filters.returnDepartureEnd} onChange={(start, end) => setFilters((p) => ({ ...p, returnDepartureStart: start, returnDepartureEnd: end }))} />
                    <TimeRangeControl label="Arrival" max={maxReturnArrivalHour} start={filters.returnArrivalStart} end={filters.returnArrivalEnd ?? maxReturnArrivalHour} onChange={(start, end) => setFilters((p) => ({ ...p, returnArrivalStart: start, returnArrivalEnd: end >= maxReturnArrivalHour ? null : end }))} />
                  </div>
                </fieldset>
              </div>

              <fieldset className="mt-4 border-t border-subtle pt-4">
                <legend className="px-1 text-xs font-medium text-secondary">Airlines</legend>
                <details className="group mt-1 max-w-md rounded-lg border border-subtle bg-surface">
                  <summary className="flex cursor-pointer list-none items-center justify-between gap-3 px-3 py-2 text-sm">
                    <span>{filters.airlines.length ? `${filters.airlines.length} airlines selected` : "All airlines"}</span>
                    <svg viewBox="0 0 20 20" className="h-4 w-4 shrink-0 text-secondary transition-transform group-open:rotate-180" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <path d="m5 7.5 5 5 5-5" />
                    </svg>
                  </summary>
                  <div className="max-h-64 overflow-y-auto border-t border-subtle p-2">
                    <button type="button" className={`mb-1 flex w-full items-center rounded-md px-2 py-2 text-left text-sm ${filters.airlines.length === 0 ? "bg-blue-500/10 font-medium text-blue-600 dark:text-blue-400" : "hover:bg-surface-secondary"}`} onClick={() => setFilters((p) => ({ ...p, airlines: [] }))}>
                      All airlines
                    </button>
                    {airlineOptions.map((airline) => (
                      <div key={airline.code} className="flex min-w-0 items-center gap-2 rounded-md px-2 py-2 hover:bg-surface-secondary">
                        <img src={airlineLogoUrl(airline.code, baseUrl) || undefined} width={20} height={20} alt="" className="h-5 w-auto max-w-7 shrink-0" style={airlineLogoRequiresWhiteBackground(airline.code) ? { background: "#fff", border: "2px solid #fff" } : undefined} loading="lazy" onError={(e) => ((e.currentTarget as HTMLImageElement).style.display = "none")} />
                        <div className="min-w-0 flex-1">
                          <Checkbox checked={filters.airlines.includes(airline.code)} label={<span className="block min-w-0 truncate text-sm" title={airline.name}>{airline.name}</span>} onCheckedChange={(checked) => setFilters((p) => ({ ...p, airlines: checked ? [...p.airlines, airline.code] : p.airlines.filter((code) => code !== airline.code) }))} />
                        </div>
                        <span className="shrink-0 text-xs tabular-nums text-secondary">{airline.count}</span>
                      </div>
                    ))}
                  </div>
                </details>
              </fieldset>

              <fieldset className="mt-4 border-t border-subtle pt-4">
                <legend className="px-1 text-xs font-medium text-secondary">Benefits</legend>
                <div className="mb-2 text-xs text-secondary">Only show itineraries with:</div>
                <div className="grid gap-2 sm:grid-cols-2 md:grid-cols-4">
                  {([
                    ["changeable", "Changes allowed"],
                    ["refundable", "Refundable"],
                    ["carryOnIncluded", "Carry-on included"],
                    ["checkedBagIncluded", "Checked bag included"],
                    ["excludeCodeshares", "Exclude codeshares"],
                  ] as const).map(([key, label]) => (
                    <div
                      key={key}
                      className={`rounded-lg border px-3 py-2 text-sm transition-colors ${filters[key] ? "border-blue-500 bg-blue-500/10" : "border-subtle bg-surface"}`}
                    >
                      <Checkbox
                        checked={filters[key]}
                        label={label}
                        onCheckedChange={(checked) => setFilters((p) => ({ ...p, [key]: checked }))}
                      />
                    </div>
                  ))}
                </div>
              </fieldset>

              <div className="mt-4 flex flex-wrap items-end justify-between gap-3 border-t border-subtle pt-4">
                <Button variant="soft" color="secondary" onClick={() => setFilters((p) => ({ ...p, minStops: null, maxStops: null, airlines: [], minPrice: null, maxPrice: null, minDurationHours: null, maxDurationHours: null, minOutboundLayoverHours: null, maxOutboundLayoverHours: null, minReturnLayoverHours: null, maxReturnLayoverHours: null, outboundDepartureStart: 0, outboundDepartureEnd: 24, outboundArrivalStart: 0, outboundArrivalEnd: null, returnDepartureStart: 0, returnDepartureEnd: 24, returnArrivalStart: 0, returnArrivalEnd: null, changeable: false, refundable: false, carryOnIncluded: false, checkedBagIncluded: false, excludeCodeshares: false }))}>Clear filters</Button>
                <label className="ml-auto w-full text-xs text-secondary sm:w-auto sm:min-w-64">
                  Sort by
                  <select className="mt-1 h-9 w-full rounded-lg border border-subtle bg-surface px-2 text-sm text-primary" value={`${filters.sortKey ?? "price"}:${filters.sortDir}`} onChange={(e) => { const [sortKey, sortDir] = e.target.value.split(":"); setFilters((p) => ({ ...p, sortKey: sortKey as SortKey, sortDir: sortDir as "asc" | "desc" })); }}>
                    <option value="price:asc">Price: low to high</option>
                    <option value="price:desc">Price: high to low</option>
                    <option value="duration:asc">Duration: shortest</option>
                    <option value="duration:desc">Duration: longest</option>
                    <option value="stops:asc">Stops: fewest</option>
                    <option value="outbound_layover:asc">Outbound layover: shortest</option>
                    <option value="outbound_layover:desc">Outbound layover: longest</option>
                    {hasReturnTrips && <option value="return_layover:asc">Return layover: shortest</option>}
                    {hasReturnTrips && <option value="return_layover:desc">Return layover: longest</option>}
                    <option value="outbound_departure:asc">Outbound departure: earliest</option>
                    <option value="outbound_departure:desc">Outbound departure: latest</option>
                    <option value="outbound_arrival:asc">Outbound arrival: earliest</option>
                    <option value="outbound_arrival:desc">Outbound arrival: latest</option>
                    <option value="return_departure:asc">Return departure: earliest</option>
                    <option value="return_departure:desc">Return departure: latest</option>
                    <option value="return_arrival:asc">Return arrival: earliest</option>
                    <option value="return_arrival:desc">Return arrival: latest</option>
                  </select>
                </label>
              </div>
            </div>
          </details>
        )}

        {/* List */}
        <div
          ref={scrollContainerRef}
          className={useVirtualScrolling ? "max-h-[90vh] overflow-y-auto" : ""}
          onScroll={
            useVirtualScrolling
              ? (e) => setScrollTop((e.target as HTMLDivElement).scrollTop)
              : undefined
          }
        >
          {topSpacerHeight > 0 && <div style={{ height: topSpacerHeight }} />}
          {/* Empty state */}
          {filteredAndSorted.length === 0 && (
            <div className="mb-3 rounded-2xl border border-subtle bg-surface shadow-lg p-6 text-center">
              <div className="heading-sm">No results match your filters</div>
              <div className="mt-1 text-sm text-secondary">
                Try widening your price, time, airline, or benefit filters.
              </div>

              <div className="mt-4 flex justify-center gap-2">
                <Button
                  variant="soft"
                  color="secondary"
                  onClick={() =>
                    setFilters((p) => ({
                      ...p,
                      minStops: null,
                      maxStops: null,
                      airlines: [],
                      minPrice: null,
                      maxPrice: null,
                      minDurationHours: null,
                      maxDurationHours: null,
                      minOutboundLayoverHours: null,
                      maxOutboundLayoverHours: null,
                      minReturnLayoverHours: null,
                      maxReturnLayoverHours: null,
                      outboundDepartureStart: 0,
                      outboundDepartureEnd: 24,
                      outboundArrivalStart: 0,
                      outboundArrivalEnd: null,
                      returnDepartureStart: 0,
                      returnDepartureEnd: 24,
                      returnArrivalStart: 0,
                      returnArrivalEnd: null,
                      changeable: false,
                      refundable: false,
                      carryOnIncluded: false,
                      checkedBagIncluded: false,
                      excludeCodeshares: false,
                    }))
                  }
                >
                  Clear filters
                </Button>
              </div>
            </div>
          )}

          {visibleItineraries.map((it, localIdx) => {
            const globalIndex = useVirtualScrolling ? startIndex + localIdx : localIdx;
            const expandedCard = isExpanded(it.id);
            const SummaryRow = ({
                                  first,
                                  last,
                                  trip,
                                  stops,
                                }: {
              first?: Flight;
              last?: Flight;
              trip?: Trip;
              stops?: number;
            }) => {
              const depTime = formatTime(first?.leavingAt);
              const arrTime = formatTime(last?.arrivingAt);

              const depDate = formatDate(first?.leavingAt);
              const arrDate = formatDate(last?.arrivingAt);
              const plus = formatDayOffset(first?.leavingAt, last?.arrivingAt);

              const depCode = String(first?.fromAirport ?? it.fromAirport ?? "").toUpperCase();
              const arrCode = String(last?.toAirport ?? it.toAirport ?? "").toUpperCase();

              const segSecs =
                typeof trip?.duration === "number"
                  ? trip.duration
                  : first?.leavingAt && last?.arrivingAt
                    ? (last.arrivingAt.getTime() - first.leavingAt.getTime()) / 1000
                    : undefined;

              const stopCodes = stopoverCodes(trip);

              return (
                <div className="w-full">
                  {/* Row 1: times + duration + lines */}
                  <div className="grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] md:grid-cols-[160px_1fr_160px] items-center gap-2">
                    {/* Left */}
                    <div className="min-w-0 justify-self-start tabular-nums">
                      <div className="flex items-baseline gap-2 min-w-0">
                        <span className="text-sm font-semibold whitespace-nowrap md:text-base">{depTime}</span>
                        <span className="hidden md:inline text-sm font-normal text-secondary truncate">
                          {depDate}
                        </span>
                      </div>
                    </div>

                    {/* Center */}
                    <div className="flex items-center gap-2 min-w-0">
                      <div className="flex-1 border-t border-subtle" />
                      <DurationBadge seconds={segSecs} />
                      <div className="flex-1 border-t border-subtle" />
                    </div>

                    {/* Right */}
                    <div className="min-w-0 justify-self-end text-right tabular-nums">
                      <div className="flex items-baseline justify-end gap-2 min-w-0">
                        <span className="text-sm font-semibold whitespace-nowrap md:text-base">{arrTime}</span>
                        <span className="hidden md:inline text-sm font-normal text-secondary truncate">
                          {arrDate}{plus}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Row 2: dates / stops / airport codes */}
                  <div className=" grid grid-cols-3 items-center text-sm text-secondary">
                    {/* Left */}
                    <div className="whitespace-nowrap flex items-center gap-2">
                      <span className="md:hidden">{depDate}</span>
                      <span className="hidden md:inline text-xs font-medium text-secondary/80">
                        {depCode}
                      </span>
                    </div>

                    {/* Center: stops + stopover airports */}
                    <div className="pt-1 flex justify-center items-center gap-2">
                      <StopsBadge stops={stops} stopCodes={stopCodes} />
                    </div>

                    {/* Right */}
                    <div className="whitespace-nowrap text-right flex items-center justify-end gap-2">
                      <span className="md:hidden">
                        {arrDate}
                        {plus}
                      </span>
                      <span className="hidden md:inline text-xs font-medium text-secondary/80">
                        {arrCode}
                      </span>
                    </div>
                  </div>

                  {/* Row 3: airport codes + stopovers (mobile only) */}
                  <div className="mt-1 grid grid-cols-3 items-center text-xs text-secondary/80 md:hidden">
                    <div className="whitespace-nowrap">{depCode}</div>
                    <div className="text-center whitespace-nowrap truncate">
                      {stopCodes?.length ? stopCodes.join(" · ") : ""}
                    </div>
                    <div className="whitespace-nowrap text-right">{arrCode}</div>
                  </div>
                </div>
              );
            };

            const registerRef = (el: HTMLDivElement | null) => {
              if (!el) return;
              const h = el.offsetHeight;
              if (!h) return;
              setRowHeights((prev) => {
                if (prev[globalIndex] === h) return prev;
                const next = prev.slice();
                next[globalIndex] = h;
                return next;
              });
            };

            const outFirst = firstFlight(it.outboundTrip);
            const outLast = lastFlight(it.outboundTrip);
            const retFirst = firstFlight(it.returnTrip);
            const retLast = lastFlight(it.returnTrip);
            const showChangeAllowed = normalizeBoolean(it.is_change_allowed) !== null;
            const showCancelAllowed = normalizeBoolean(it.is_cancel_allowed) !== null;
            const showCarryOnBaggage = normalizeNumber(it.carry_on_baggage_included) !== null;
            const showCheckedBaggage = normalizeNumber(it.checked_baggage_included) !== null;
            const showCo2Emissions = normalizeNumber(it.co2_emissions) !== null;
            const showExtraDetails =
              showChangeAllowed || showCancelAllowed || showCarryOnBaggage || showCheckedBaggage || showCo2Emissions;
            const changeable = normalizeBoolean(it.is_change_allowed) === true;
            const refundable = normalizeBoolean(it.is_cancel_allowed) === true;
            const changeFee = normalizeNumber(it.fare_change_fee);
            const refundFee = normalizeNumber(it.fare_cancel_fee);
            const changeTooltip = changeFee == null
              ? "Changes allowed (fee may apply)"
              : `Changes allowed (${formatMoneyWithCurrency(changeFee, it.fare_change_fee_currency)})`;
            const refundTooltip = refundFee == null
              ? "Refundable (fee may apply)"
              : `Refundable (${formatMoneyWithCurrency(refundFee, it.fare_cancel_fee_currency)})`;
            const carryOnBags = normalizeNumber(it.carry_on_baggage_included) ?? 0;
            const checkedBags = normalizeNumber(it.checked_baggage_included) ?? 0;
            const outStops = it.outboundTrip?.flights ? Math.max(0, it.outboundTrip.flights.length - 1) : (it.totalStops ?? 0);
            const retStops = it.returnTrip?.flights ? Math.max(0, it.returnTrip.flights.length - 1) : undefined;
            const operators = codeshareOperators(it);
            const primaryOperator = operators[0];

            return (
              <div
                key={String(it.id ?? globalIndex)}
                ref={registerRef}
                className="relative mb-3 rounded-2xl border border-subtle bg-surface shadow-lg overflow-hidden"
                tabIndex={0}
                onClick={(e) => {
                  const el = e.target as HTMLElement | null;
                   if (el?.closest("button,a,input,select,textarea,label,[data-itinerary-tooltip-trigger]")) return;
                  toggleExpanded(it.id);
                }}
              >
                {!showAirlineFilter && (
                  <div
                    className="absolute left-0 top-0 h-9 w-9"
                    title={`Itinerary ID: ${it.id}`}
                    aria-label={`Itinerary ID ${it.id}`}
                  >
                    <span
                      className="absolute left-0 top-0 h-9 w-9"
                      style={{
                        backgroundColor: "var(--color-background-secondary-soft-alpha, rgba(148, 163, 184, 0.18))",
                        clipPath: "polygon(0 0, 100% 0, 0 100%)",
                      }}
                      aria-hidden="true"
                    />
                    <span
                      className="absolute left-1.5 top-1 text-xs font-bold leading-none tabular-nums text-secondary text-center"
                      style={{
                        width: "15px"
                      }}>
                      {it.id}
                    </span>
                  </div>
                )}

                {/* BODY */}
                <div className="p-3">
                  <div className="flex gap-3">
                    {/* left rail */}
                    <div className="min-w-[90px] text-center flex flex-col items-center">
                      <div className="heading-md w-full leading-tight">
                        <div>{fmtCurrency(it.price)}</div>
                        {showCabinClass && it.cabin_class && (
                          <div className="text-[11px] text-secondary text-center">
                            {formatCabinClass(it.cabin_class)}
                          </div>
                        )}
                      </div>
                      <div className="mt-3 flex flex-col items-center gap-2">
                        <img
                          src={airlineLogoUrl(it.airlineCode, baseUrl) || undefined}
                          width={22}
                          height={22}
                          alt={it.airlineName || it.airlineCode || ""}
                          title={it.airlineName || it.airlineCode || ""}
                          className="max-h-8 max-w-[80px]"
                          style={airlineLogoRequiresWhiteBackground(it.airlineCode) ? {
                            background: "#fff",
                            border: "3px solid #fff",
                            boxShadow: "0 0 7px 2px rgba(255, 255, 255, 0.33)",
                          } : undefined}
                          loading="lazy"
                          onError={(e) => {
                            // hide broken logos
                            (e.currentTarget as HTMLImageElement).style.display = "none";
                          }}
                        />
                        <div className="text-secondary text-xs w-full max-w-[80px] text-center overflow-hidden text-ellipsis line-clamp-2">
                          {it.airlineName || it.airlineCode || ""}
                        </div>
                        {(changeable || refundable || carryOnBags > 0 || checkedBags > 0) && (
                          <div className="flex items-center justify-center gap-1 text-gray-300 dark:text-gray-300 md:gap-2">
                            {changeable && (
                              <TappableTooltip content={changeTooltip}>
                                <button type="button" className="itinerary-benefit-icon inline-flex" aria-label={changeTooltip}>
                                  <svg viewBox="0 0 24 24" className="h-3.5 w-3.5 md:h-4 md:w-4" fill="none" stroke="var(--itinerary-benefit-icon-color, #a8b0bd)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                    <path d="M5 8h14" />
                                    <path d="m16 5 3 3-3 3" />
                                    <path d="M19 16H5" />
                                    <path d="m8 13-3 3 3 3" />
                                  </svg>
                                </button>
                              </TappableTooltip>
                            )}
                            {refundable && (
                              <TappableTooltip content={refundTooltip}>
                                <button type="button" className="itinerary-benefit-icon inline-flex" aria-label={refundTooltip}>
                                  <svg viewBox="0 0 24 24" className="h-3.5 w-3.5 md:h-4 md:w-4" fill="none" stroke="var(--itinerary-benefit-icon-color, #a8b0bd)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                    <circle cx="12" cy="12" r="9" />
                                    <path d="M12 6v12" />
                                    <path d="M15 8.5C15 7.7 13.7 7 12 7s-3 .9-3 2 1.3 2 3 2 3 .9 3 2-1.3 2-3 2-3-.7-3-1.5" />
                                  </svg>
                                </button>
                              </TappableTooltip>
                            )}
                            {carryOnBags > 0 && (
                              <TappableTooltip content={`${carryOnBags} carry-on ${carryOnBags === 1 ? "bag" : "bags"} included`}>
                                <button type="button" className="itinerary-benefit-icon relative inline-flex h-4 w-4 md:h-5 md:w-5" aria-label={`${carryOnBags} carry-on ${carryOnBags === 1 ? "bag" : "bags"} included`}>
                                  <svg viewBox="0 0 24 24" className="relative h-4 w-4 md:h-5 md:w-5" fill="var(--itinerary-benefit-icon-color, #a8b0bd)" aria-hidden="true">
                                    <path d="M9 2a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v5h.5A2.5 2.5 0 0 1 18 9.5v8a3.5 3.5 0 0 1-3 3.46V22a1 1 0 1 1-2 0v-1h-2v1a1 1 0 1 1-2 0v-1.04a3.5 3.5 0 0 1-3-3.46v-8A2.5 2.5 0 0 1 8.5 7H9V2Zm2 1v4h2V3h-2Z" />
                                  </svg>
                                </button>
                              </TappableTooltip>
                            )}
                            {checkedBags > 0 && (
                              <TappableTooltip content={`${checkedBags} checked ${checkedBags === 1 ? "bag" : "bags"} included`}>
                                <button type="button" className="itinerary-benefit-icon relative inline-flex h-4 w-4 md:h-5 md:w-5" aria-label={`${checkedBags} checked ${checkedBags === 1 ? "bag" : "bags"} included`}>
                                  <svg viewBox="0 0 24 24" className="relative h-4 w-4 md:h-5 md:w-5" fill="var(--itinerary-benefit-icon-color, #a8b0bd)" aria-hidden="true">
                                    <path d="M9 3h6a1 1 0 0 1 1 1v2h1.5A3.5 3.5 0 0 1 21 9.5v8a3.5 3.5 0 0 1-3 3.46V22a1 1 0 1 1-2 0v-1H8v1a1 1 0 1 1-2 0v-1.04a3.5 3.5 0 0 1-3-3.46v-8A3.5 3.5 0 0 1 6.5 6H8V4a1 1 0 0 1 1-1Z" />
                                  </svg>
                                </button>
                              </TappableTooltip>
                            )}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* right content */}
                    <div className="flex-1 space-y-3">
                      <SummaryRow
                        first={outFirst}
                        last={outLast}
                        trip={it.outboundTrip}
                        stops={outStops}
                      />

                      {it.returnTrip && (
                        <div className="pt-1">
                          <SummaryRow
                            first={retFirst}
                            last={retLast}
                            trip={it.returnTrip}
                            stops={retStops}
                          />
                        </div>
                      )}

                      {primaryOperator && (
                        <div className="text-[11px] text-secondary md:text-xs">
                          <div className="grid grid-cols-[auto_minmax(0,1fr)] items-center gap-x-1.5 gap-y-1 md:hidden">
                            <span className="shrink-0">Operated by</span>
                            <div className="flex min-w-0 items-center gap-1.5">
                              {airlineLogoUrl(primaryOperator.operatingAirlineCode, baseUrl) && (
                                <img
                                  src={airlineLogoUrl(primaryOperator.operatingAirlineCode, baseUrl) || undefined}
                                  width={16}
                                  height={16}
                                  alt=""
                                  className="h-4 w-auto max-w-6 shrink-0"
                                  style={airlineLogoRequiresWhiteBackground(primaryOperator.operatingAirlineCode) ? {
                                    background: "#fff",
                                    border: "2px solid #fff",
                                  } : undefined}
                                  loading="lazy"
                                  onError={(e) => ((e.currentTarget as HTMLImageElement).style.display = "none")}
                                />
                              )}
                              <span className="truncate font-medium" title={primaryOperator.operatingAirlineName}>
                                {primaryOperator.operatingAirlineName}
                              </span>
                            </div>
                            {operators.length === 2 && (
                              <>
                                <span />
                                <div className="flex min-w-0 items-center gap-1.5">
                                  {airlineLogoUrl(operators[1].operatingAirlineCode, baseUrl) && (
                                    <img
                                      src={airlineLogoUrl(operators[1].operatingAirlineCode, baseUrl) || undefined}
                                      width={16}
                                      height={16}
                                      alt=""
                                      className="h-4 w-auto max-w-6 shrink-0"
                                      loading="lazy"
                                      onError={(e) => ((e.currentTarget as HTMLImageElement).style.display = "none")}
                                    />
                                  )}
                                  <span className="truncate font-medium" title={operators[1].operatingAirlineName}>
                                    {operators[1].operatingAirlineName}
                                  </span>
                                </div>
                              </>
                            )}
                            {operators.length >= 3 && (
                              <>
                                <span />
                                <span>+ others</span>
                              </>
                            )}
                          </div>

                          <div className="hidden items-center gap-1.5 md:flex">
                          <span>Includes flights operated by</span>
                          {airlineLogoUrl(primaryOperator.operatingAirlineCode, baseUrl) && (
                            <img
                              src={airlineLogoUrl(primaryOperator.operatingAirlineCode, baseUrl) || undefined}
                              width={16}
                              height={16}
                              alt=""
                              className="h-4 w-auto max-w-6 shrink-0"
                              style={airlineLogoRequiresWhiteBackground(primaryOperator.operatingAirlineCode) ? {
                                background: "#fff",
                                border: "2px solid #fff",
                              } : undefined}
                              loading="lazy"
                              onError={(e) => ((e.currentTarget as HTMLImageElement).style.display = "none")}
                            />
                          )}
                          <span className="min-w-0 truncate font-medium" title={primaryOperator.operatingAirlineName}>
                            {primaryOperator.operatingAirlineName}
                          </span>
                          {operators.length === 2 && (
                            <>
                              {airlineLogoUrl(operators[1].operatingAirlineCode, baseUrl) && (
                                <img
                                  src={airlineLogoUrl(operators[1].operatingAirlineCode, baseUrl) || undefined}
                                  width={16}
                                  height={16}
                                  alt=""
                                  className="h-4 w-auto max-w-6 shrink-0"
                                  style={airlineLogoRequiresWhiteBackground(operators[1].operatingAirlineCode) ? {
                                    background: "#fff",
                                    border: "2px solid #fff",
                                  } : undefined}
                                  loading="lazy"
                                  onError={(e) => ((e.currentTarget as HTMLImageElement).style.display = "none")}
                                />
                              )}
                              <span className="min-w-0 truncate font-medium" title={operators[1].operatingAirlineName}>
                                {operators[1].operatingAirlineName}
                              </span>
                            </>
                          )}
                          {operators.length >= 3 && (
                            <span className="shrink-0">and others</span>
                          )}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* RIGHT ACTIONS (md+ only) */}
                    <div className="hidden md:flex w-[125px] flex-col justify-between items-stretch">
                      <ButtonLink
                        href={`${baseBookingUrl}&offer_id=${encodeURIComponent(String(it.offer_id))}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        external={true}
                        onClick={(e) => e.stopPropagation()}
                      >
                        Book
                      </ButtonLink>

                      <Button
                        variant="soft"
                        color="secondary"
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleExpanded(it.id);
                        }}
                      >
                        {expandedCard ? "Details ▲" : "Details ▼"}
                      </Button>
                    </div>
                  </div>

                </div>

                {/* FOOTER (still INSIDE the card) */}
                <div className="border-t border-subtle bg-surface">
                  {/* actions */}
                  <div className="flex md:hidden items-center justify-between gap-3 px-4 py-3">
                    <Button
                      variant="soft"
                      color="secondary"
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleExpanded(it.id);
                      }}
                    >
                      {expandedCard ? "Details ▲" : "Details ▼"}
                    </Button>

                    <ButtonLink
                      href={`${baseBookingUrl}&offer_id=${encodeURIComponent(String(it.offer_id))}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      external={true}
                      onClick={(e) => e.stopPropagation()}
                    >
                      Book
                    </ButtonLink>
                  </div>

                  {/* expanded details (full width, inside footer, inside card) */}
                  {expandedCard && (
                    <div
                      className="border-t border-subtle px-2 py-2 text-sm"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div className="grid gap-4 md:grid-cols-2">
                        <TripDetails title="Outbound" trip={it.outboundTrip} baseUrl={baseUrl} />
                        {it.returnTrip && <TripDetails title="Return" trip={it.returnTrip} baseUrl={baseUrl} />}
                      </div>

                      {showExtraDetails && (
                        <div className="mt-3 rounded-2xl border border-subtle bg-surface p-3">
                          <div className="text-xs font-medium uppercase tracking-wide text-secondary">
                            Extra details
                          </div>
                          <div className="mt-2 grid gap-2 md:grid-cols-2">
                            {(showCarryOnBaggage || showCheckedBaggage) && (
                              <div className="rounded-xl border border-subtle bg-surface px-3 py-2">
                                <div className="text-xs text-secondary">Baggage</div>
                                <div className="mt-0.5 font-medium">{formatBaggageIncluded(it.carry_on_baggage_included, "Carry-on")}</div>
                                <div className="mt-0.5 font-medium">{formatBaggageIncluded(it.checked_baggage_included, "Checked")}</div>
                              </div>
                            )}
                            {showChangeAllowed && (
                              <div className="rounded-xl border border-subtle bg-surface px-3 py-2">
                                <div className="text-xs text-secondary">Changes</div>
                                <div className="mt-0.5 font-medium">{formatChangesWithOptionalFee(it.is_change_allowed, it.fare_change_fee, it.fare_change_fee_currency)}</div>
                              </div>
                            )}
                            {showCancelAllowed && (
                              <div className="rounded-xl border border-subtle bg-surface px-3 py-2">
                                <div className="text-xs text-secondary">Cancellation</div>
                                <div className="mt-0.5 font-medium">{formatCancellationWithOptionalFee(it.is_cancel_allowed, it.fare_cancel_fee, it.fare_cancel_fee_currency)}</div>
                              </div>
                            )}
                            {showCo2Emissions && (
                              <div className="rounded-xl border border-subtle bg-surface px-3 py-2">
                                <div className="text-xs text-secondary">CO2 emissions</div>
                                <div className="mt-0.5 font-medium">{formatCo2Emissions(it.co2_emissions)}</div>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })}

          {bottomSpacerHeight > 0 && <div style={{ height: bottomSpacerHeight }} />}
        </div>
        {fullDisplayUrl && (
          <div className="text-end ms-1 text-body mt-3">
            <ButtonLink
              target="_blank"
              rel="noopener noreferrer"
              href={fullDisplayUrl}
              external={true}
            >
              {totalItinerariesCount != null
                ? `View all ${totalItinerariesCount} itineraries`
                : "View all itineraries"}
            </ButtonLink>
          </div>
        )}
      </div>
    </div>
  );
}

function TripDetails({ title, trip, baseUrl }: { title: string; trip?: Trip; baseUrl?: string }) {
  const flights = trip?.flights ?? [];
  if (!flights.length) return null;

  const first = flights[0];
  const last = flights[flights.length - 1];

  const SegmentRow = ({ f }: { f: Flight }) => {
    const depTime = formatTime(f.leavingAt);
    const arrTime = formatTime(f.arrivingAt);

    const depDate = formatDate(f.leavingAt);
    const arrDate = formatDate(f.arrivingAt);
    const plus = formatDayOffset(f.leavingAt, f.arrivingAt) ?? "test";

    const depCode = String(f.fromAirport ?? "").toUpperCase();
    const arrCode = String(f.toAirport ?? "").toUpperCase();

    return (
      <div className="w-full">
        {/* Row 1: times + duration + lines (same as summary) */}
        <div className="flex items-center gap-2">
          <div className="text-sm font-semibold whitespace-nowrap">{depTime}</div>

          <div className="flex-1 flex items-center gap-2 min-w-0">
            <div className="flex-1 border-t border-subtle" />
            <DurationBadge seconds={f.duration} />
            <div className="flex-1 border-t border-subtle" />
          </div>

          <div className="text-sm font-semibold whitespace-nowrap text-right">{arrTime}</div>
        </div>

        {/* Row 2: dates */}
        <div className="mt-1 flex items-center justify-between text-xs text-secondary">
          <div className="whitespace-nowrap">{depDate}</div>
          <div className="whitespace-nowrap text-right">
            {arrDate}
          </div>
        </div>

        {/* Row 3: airport names */}
        <div className="mt-1 flex justify-between gap-3 text-[11px] text-secondary/80">
          <div
            className="truncate"
            title={f.fromAirportName || f.fromAirport || ""}
          >
            {f.fromAirportName || f.fromAirport || ""}
          </div>

          <div
            className="truncate text-right"
            title={f.toAirportName || f.toAirport || ""}
          >
            {f.toAirportName || f.toAirport || ""}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="rounded-2xl border border-subtle bg-surface p-3">
      <div className="flex items-center justify-between gap-2 text-xs text-secondary">
        <div className="min-w-0 truncate">
          <span className="font-medium">{title}</span>{" "}
          <span className="text-secondary/80">
            {first.fromAirport} → {last.toAirport}
          </span>
          <span className="ps-1">
            <DurationBadge className="ps-1" seconds={trip?.duration} />
          </span>
        </div>
      </div>

      <div className="mt-3 space-y-3">
        {flights.map((f, idx) => {
          const lay =
            typeof f.stopoverDuration === "number" && f.stopoverDuration > 0
              ? fmtHM(f.stopoverDuration)
              : null;
          const wifiCost =
            typeof f.wifi_cost === "number"
              ? `$${f.wifi_cost.toFixed(2)}`
              : typeof f.wifi_cost === "string" && f.wifi_cost.trim().length > 0
                ? f.wifi_cost
                : null;
          const wifiCostText = typeof wifiCost === "string" ? wifiCost.trim() : null;
          const wifiIsFree =
            wifiCostText !== null &&
            ["free", "0", "0.0", "0.00", "$0", "$0.0", "$0.00"].includes(wifiCostText.toLowerCase());
          const wifiAvailable = f.wifi_available === true;
          const wifiFeeLabel = wifiAvailable && !wifiIsFree ? "(fee)" : null;
          const wifiTooltip = wifiIsFree
            ? "Wi-Fi: free"
            : wifiCostText
              ? `Wi-Fi: ${wifiCostText}`
              : "Wi-Fi available for a fee";
          const powerAvailable = f.power_available === true;

          return (
            <Fragment key={idx}>
              {lay && (
                <div className="rounded-xl border border-subtle bg-surface px-3 py-2 text-xs text-secondary">
                  Layover {lay}
                </div>
              )}

              <div className="rounded-xl border border-subtle bg-surface px-3 py-2">
                {/* airline + flight */}
                <div className="text-sm">
                  <div className="flex min-w-0 items-start gap-2">
                    <img
                      src={airlineLogoUrl(f.airlineCode, baseUrl) || undefined}
                      width={18}
                      height={18}
                      alt={f.airlineName || f.airlineCode || "Airline"}
                      title={f.airlineName || f.airlineCode || ""}
                      className="h-4 w-auto max-w-6 shrink-0"
                      style={airlineLogoRequiresWhiteBackground(f.airlineCode) ? {
                        background: "#fff",
                        border: "2px solid #fff",
                        boxShadow: "0 0 5px 2px rgba(255, 255, 255, 0.30)",
                      } : undefined}
                      loading="lazy"
                      onError={(e) => ((e.currentTarget as HTMLImageElement).style.display = "none")}
                    />
                    <div className="min-w-0 flex-1">
                      <div className="flex min-w-0 items-start justify-between gap-3">
                        <div className="font-medium truncate">
                          {(f.airlineCode || "").toUpperCase()}
                          {f.flightNumber || ""}
                        </div>
                        <div className="flex shrink-0 items-center gap-2 text-xs text-secondary">
                          {f.aircraftName && <span className="max-w-40 truncate" title={f.aircraftName}>{f.aircraftName}</span>}
                          <div className="inline-flex items-center gap-1.5">
                            {powerAvailable && (
                              <TappableTooltip content="Power outlet available">
                                <button type="button" className="inline-flex items-center" aria-label="Power outlet available">
                                  <svg viewBox="0 0 200 200" className="h-4 w-4" fill="currentColor" aria-hidden="true">
                                    <path d="M150 58.33h-12.5v45c0 11.97-9.7 21.67-21.67 21.67h.83v12.5h-8.33v50H91.66v-50h-8.33V125h.83c-11.97 0-21.67-9.7-21.67-21.67v-45h-12.5V41.66h20.83V20.83a8.33 8.33 0 0116.66 0v20.83h25V20.83a8.33 8.33 0 0116.66 0v20.83h20.83v16.67z" />
                                  </svg>
                                </button>
                              </TappableTooltip>
                            )}
                            {wifiAvailable && (
                              <TappableTooltip content={wifiTooltip}>
                                <button type="button" className="inline-flex items-center gap-1" aria-label={wifiTooltip}>
                                  <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                    <path d="M5 12.55a11 11 0 0 1 14.08 0" />
                                    <path d="M1.42 9a16 16 0 0 1 21.16 0" />
                                    <path d="M8.53 16.11a6 6 0 0 1 6.95 0" />
                                    <line x1="12" y1="20" x2="12.01" y2="20" />
                                  </svg>
                                  {wifiFeeLabel && <span>{wifiFeeLabel}</span>}
                                </button>
                              </TappableTooltip>
                            )}
                          </div>
                        </div>
                      </div>
                      {f.codeshare && f.operatingAirlineName && (
                        <div className="mt-1 flex min-w-0 items-center gap-1.5 text-xs text-secondary">
                          <span className="shrink-0">Operated by</span>
                          {airlineLogoUrl(f.operatingAirlineCode, baseUrl) && (
                            <img
                              src={airlineLogoUrl(f.operatingAirlineCode, baseUrl) || undefined}
                              width={16}
                              height={16}
                              alt=""
                              className="h-4 w-auto max-w-6 shrink-0"
                              style={airlineLogoRequiresWhiteBackground(f.operatingAirlineCode) ? {
                                background: "#fff",
                                border: "2px solid #fff",
                              } : undefined}
                              loading="lazy"
                              onError={(e) => ((e.currentTarget as HTMLImageElement).style.display = "none")}
                            />
                          )}
                          <span className="truncate" title={f.operatingAirlineName}>{f.operatingAirlineName}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* NEW: segment timeline row with lines + duration badge */}
                <div className="mt-2">
                  <SegmentRow f={f} />
                </div>
              </div>
            </Fragment>
          );
        })}
      </div>
    </div>
  );
}

const rootEl = document.getElementById("root");

if (rootEl) {
  createRoot(rootEl).render(
    <React.StrictMode>
      <AppsSDKUIProvider>
        <ItineraryWidget />
      </AppsSDKUIProvider>
    </React.StrictMode>
  );
}
